// EXIF GPS extraction + pairwise distance math for multi-photo inspection.
//
// Browser-only: uses `exifr` (bundled with the client). Backend never reads
// EXIF — admin-only path, defense-in-depth on the server would be theater.

import exifr from 'exifr';

const EARTH_RADIUS_METERS = 6371000;

/**
 * Read GPS coordinates from a single File's EXIF block.
 * Returns { lat, lng } or null if no GPS data is present (or unreadable).
 */
export async function readGps(file) {
  try {
    const result = await exifr.gps(file);
    if (!result || typeof result.latitude !== 'number' || typeof result.longitude !== 'number') {
      return null;
    }
    return { lat: result.latitude, lng: result.longitude };
  } catch {
    return null;
  }
}

/**
 * Great-circle distance in meters between two lat/lng points.
 */
export function haversineMeters(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h));
}

/**
 * Returns the maximum pairwise distance (meters) among an array of points,
 * plus the [i, j] indices of the farthest pair. A 0/1-point array returns
 * { maxMeters: 0, farthestPair: null }.
 */
export function maxPairwiseDistance(points) {
  if (!Array.isArray(points) || points.length < 2) {
    return { maxMeters: 0, farthestPair: null };
  }
  let maxMeters = 0;
  let farthestPair = [0, 1];
  for (let i = 0; i < points.length; i += 1) {
    for (let j = i + 1; j < points.length; j += 1) {
      const d = haversineMeters(points[i], points[j]);
      if (d > maxMeters) {
        maxMeters = d;
        farthestPair = [i, j];
      }
    }
  }
  return { maxMeters, farthestPair };
}
